import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, getErrorMessage } from '../api/client';
import { approveAllCountLines, cancelCountSheet, getCountSheets, getMyCountSheets, refreshCountSales, submitCountSheet } from '../api/countSheets';
import { useAuth } from '../auth/AuthProvider';
import { canConfigureWasteThreshold, canManageCountSheets } from '../auth/permissions';
import { BlindCountModal } from '../components/BlindCountModal';
import { CountLineApproveModal, refreshSalesErrorMessage } from '../components/CountLineApproveModal';
import { CountLineReviewModal } from '../components/CountLineReviewModal';
import { CountSheetCreateModal, type CountSheetDraft } from '../components/CountSheetCreateModal';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { EmptyState } from '../components/ui/EmptyState';
import { ActionBar } from '../components/ui/ActionBar';
import { Icon } from '../components/ui/Icon';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionTabs } from '../components/ui/SectionTabs';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Button, LoadingPanel } from '../components/ui/primitives';
import type { AssignedCountLine, AssignedCountSheet, CountSheetList, ManagerCountLine, ManagerCountSheet } from '../types/countSheets';
import {
  COUNT_LINE_LABELS, COUNT_SHEET_LABELS, appliedText, approvableInBulk, approvalComparison, assignedProgress, atApprovalText, countedText, lineBadgeVariant, nextBulkAck, plural,
  repeatDraft, salesRefreshNeed, salesRefreshText, sheetBadgeVariant, sheetProgressText, sortSheetsForManager, submitState,
} from '../utils/countSheets';
import { countThresholdSummary } from '../utils/inventoryCount';
import { useLoyversePresence } from '../utils/loyversePresence';
import { resolveStoreTimezone } from '../utils/timezones';
import { formatWasteDateTime } from '../utils/wasteReport';
import { formatWasteQuantity } from '../utils/wasteReview';

// F4 · pantalla «Conteos», FUERA de Inventario. Quien cuenta (cualquier rol) ve sus listas y cuenta a
// ciegas: nunca ve lo que dice el sistema. OWNER/MANAGER además arman listas, las asignan y aprueban,
// piden recontar o rechazan lo contado. El inventario solo cambia al aprobar.

const MANAGER_TABS = [['review', 'Por revisar'], ['closed', 'Terminadas']] as const;
type ManagerTab = (typeof MANAGER_TABS)[number][0];

export function CountSheetsPage() {
  const { user, stores: authStores } = useAuth();
  const confirm = useConfirm();
  const manager = canManageCountSheets(user?.role);
  // El umbral de diferencia grande lo cambia solo el dueño, y vive en Inventario → Stock.
  const owner = canConfigureWasteThreshold(user?.role);
  // ZTV-04: sin Loyverse, la confirmación de «Aprobar todo» no habla de sus ventas. true/null: como siempre.
  const loyverseConnected = useLoyversePresence();
  const [tab, setTab] = useState<ManagerTab>('review');
  const [storeFilter, setStoreFilter] = useState('');
  const [list, setList] = useState<CountSheetList>();
  const [mine, setMine] = useState<AssignedCountSheet[]>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  // Lo que no salió del todo bien al actualizar ventas (p. ej. una venta sin receta): aviso, no error.
  const [warning, setWarning] = useState<string>();
  const [createDraft, setCreateDraft] = useState<Partial<CountSheetDraft> | null>(null);
  const [countTarget, setCountTarget] = useState<{ line: AssignedCountLine; storeName: string } | null>(null);
  const [approveTarget, setApproveTarget] = useState<{ line: ManagerCountLine; storeId: string } | null>(null);
  const [reviewTarget, setReviewTarget] = useState<{ line: ManagerCountLine; mode: 'reject' | 'recount' } | null>(null);
  const [busyId, setBusyId] = useState<string>();
  const [refreshingId, setRefreshingId] = useState<string>();
  // Resultado de «Aprobar todo», como lista (una razón por insumo y, si se usó, lo que hizo «Actualizar ventas»).
  const [bulkReport, setBulkReport] = useState<{ approved: number; note: string | null; left: Array<{ lineId: string; ingredientName: string; message: string | null }> } | null>(null);
  // Solo la respuesta de la ÚLTIMA carga se pinta (cambiar de pestaña o tienda rápido no mezcla resultados).
  const loadSeq = useRef(0);

  // Cualquier otro aviso reemplaza al resultado de «Aprobar todo».
  useEffect(() => { if (notice) { setBulkReport(null); setWarning(undefined); } }, [notice]);

  // Devuelve la bandeja que se leyó (undefined si otra carga más nueva la reemplazó): aprobar la usa para comparar.
  const load = useCallback(async (): Promise<CountSheetList | undefined> => {
    const current = ++loadSeq.current;
    setLoading(true);
    setError(undefined);
    const [listResult, mineResult] = await Promise.allSettled([
      manager ? getCountSheets(tab === 'review' ? 'ACTIVE' : 'CLOSED', storeFilter || undefined) : Promise.resolve(undefined),
      getMyCountSheets(),
    ]);
    if (current !== loadSeq.current) return undefined;
    if (listResult.status === 'fulfilled' && listResult.value) setList(listResult.value);
    if (mineResult.status === 'fulfilled') setMine(mineResult.value.sheets);
    const failure = [listResult, mineResult].find((result): result is PromiseRejectedResult => result.status === 'rejected');
    if (failure) setError(getErrorMessage(failure.reason, 'No fue posible cargar los conteos.'));
    setLoading(false);
    return listResult.status === 'fulfilled' ? listResult.value : undefined;
  }, [manager, tab, storeFilter]);

  useEffect(() => {
    void load();
    return () => { loadSeq.current += 1; };
  }, [load]);

  // Tras «Actualizar ventas» se vuelve a leer la bandeja: el insumo que se está aprobando toma lo recalculado.
  useEffect(() => {
    if (!list) return;
    setApproveTarget((current) => {
      if (!current) return current;
      const fresh = list.sheets.flatMap((sheet) => sheet.lines).find((line) => line.id === current.line.id);
      return fresh && fresh.status === 'SUBMITTED' && fresh !== current.line ? { ...current, line: fresh } : current;
    });
  }, [list]);

  // Se recarga primero y el mensaje se pone después: la carga limpia los errores al empezar.
  const reloadWithError = (message: string) => { void load().then(() => setError(message)); };
  const timeZoneOf = (storeId: string) => resolveStoreTimezone(authStores, { storeId });
  const stores = list?.stores ?? authStores.filter((store) => store.isActive).map((store) => ({ id: store.id, name: store.name }));
  const openMine = (mine ?? []).filter((sheet) => sheet.status === 'OPEN');
  const recentMine = (mine ?? []).filter((sheet) => sheet.status !== 'OPEN');
  const managerSheets = list ? sortSheetsForManager(list.sheets) : [];

  const submit = async (sheet: AssignedCountSheet) => {
    const state = submitState(sheet);
    if (!state.canSubmit || busyId) return;
    const ok = await confirm({
      title: '¿Enviar la lista a tu gerente?',
      description: state.uncounted.length ? `No contaste: ${state.uncounted.join(', ')}. Se enviarán como «no contado».` : 'Después ya no podrás cambiar lo que contaste.',
      confirmLabel: 'Enviar',
      cancelLabel: 'Revisar',
    });
    if (!ok) return;
    setBusyId(sheet.id);
    setError(undefined);
    try {
      const result = await submitCountSheet(sheet.id);
      setNotice(result.replayed ? 'La lista ya estaba enviada.' : `Lista enviada: ${plural(result.counted, 'producto contado', 'productos contados')}. Tu gerente la revisa.`);
      await load();
    } catch (submitError) {
      reloadWithError(getErrorMessage(submitError, 'No fue posible enviar la lista.'));
    } finally {
      setBusyId(undefined);
    }
  };

  const cancel = async (sheet: ManagerCountSheet) => {
    if (busyId) return;
    const ok = await confirm({
      title: '¿Cancelar la lista?',
      description: `${sheet.assignedTo?.name ?? 'La persona'} ya no verá los insumos por contar. Lo que ya envió sigue esperando tu revisión.`,
      confirmLabel: 'Cancelar lista',
      cancelLabel: 'Volver',
      danger: true,
    });
    if (!ok) return;
    setBusyId(sheet.id);
    setError(undefined);
    try {
      await cancelCountSheet(sheet.id);
      setNotice('Lista cancelada. El inventario no cambió.');
      await load();
    } catch (cancelError) {
      reloadWithError(getErrorMessage(cancelError, 'No fue posible cancelar la lista.'));
    } finally {
      setBusyId(undefined);
    }
  };

  const approveAll = async (sheet: ManagerCountSheet) => {
    if (busyId) return;
    const ok = await confirm({
      title: 'Aprobar todo',
      description: loyverseConnected !== false
        ? 'Se aprueban los conteos cuya diferencia no es grande; si hace falta, antes se revisan las ventas de Loyverse. Los grandes se quedan para que los apruebes aparte con un motivo.'
        : 'Se aprueban los conteos cuya diferencia no es grande. Los grandes se quedan para que los apruebes aparte con un motivo.',
      confirmLabel: 'Aprobar',
      cancelLabel: 'Volver',
    });
    if (!ok) return;
    setBusyId(sheet.id);
    setError(undefined);
    setNotice(undefined);
    setBulkReport(null);
    setWarning(undefined);
    try {
      const acknowledged = { staleSalesSyncAcknowledged: false, pendingSupplyAcknowledged: false };
      let result = await approveAllCountLines(sheet.id);
      let approved = result.summary.approved;
      // Loyverse atrasado o ventas de antes sin descontar (decisión del dueño): primero se revisan las ventas
      // SOLAS, una vez, y se vuelve a aprobar. «Aprobar de todos modos» solo sale si Loyverse no se pudo revisar.
      let salesNote: string | null = null;
      if (result.results.some((item) => item.status === 'sales_sync_ack_missing' || item.status === 'pending_sales')) {
        let checked = false;
        try {
          salesNote = salesRefreshText(await refreshCountSales(sheet.store.id)).text;
          checked = true;
        } catch (refreshError) {
          salesNote = refreshSalesErrorMessage(refreshError);
        }
        if (checked) {
          result = await approveAllCountLines(sheet.id, acknowledged);
          approved += result.summary.approved;
        }
      }
      // Cada aviso se pregunta por separado (primero Loyverse, luego compras pendientes) y solo se confirma
      // el que se vio: nunca se acepta de pasada un aviso que nadie leyó.
      for (let pending = nextBulkAck(result.results); pending; pending = nextBulkAck(result.results)) {
        const alreadyAccepted = pending.kind === 'sales_sync_ack_missing' ? acknowledged.staleSalesSyncAcknowledged : acknowledged.pendingSupplyAcknowledged;
        if (alreadyAccepted) break;
        const accepted = await confirm({
          title: pending.kind === 'sales_sync_ack_missing' ? 'No se pudo revisar Loyverse' : 'Hay compras o transferencias pendientes',
          // Primero se dice qué pasó al revisar las ventas.
          description: pending.kind === 'sales_sync_ack_missing' && salesNote ? `${salesNote} ${pending.message}` : pending.message,
          confirmLabel: 'Aprobar de todos modos',
          cancelLabel: 'Dejarlos esperando',
        });
        if (!accepted) break;
        if (pending.kind === 'sales_sync_ack_missing') acknowledged.staleSalesSyncAcknowledged = true;
        else acknowledged.pendingSupplyAcknowledged = true;
        result = await approveAllCountLines(sheet.id, acknowledged);
        approved += result.summary.approved;
      }
      setBulkReport({ approved, note: salesNote, left: result.results.filter((item) => item.message).map((item) => ({ lineId: item.lineId, ingredientName: item.ingredientName, message: item.message })) });
      await load();
    } catch (approveError) {
      if (approveError instanceof ApiError && approveError.status >= 400 && approveError.status < 500) {
        reloadWithError(getErrorMessage(approveError, 'No fue posible aprobar la lista.'));
      } else {
        // El servidor sigue aprobando uno por uno aunque la pantalla deje de esperar.
        reloadWithError('No sabemos si se aprobaron todos: se perdió la conexión o el servidor tardó demasiado. Revisa la lista; lo que ya se aprobó no se aplica dos veces.');
      }
    } finally {
      setBusyId(undefined);
    }
  };

  // «Actualizar ventas» (dueño o gerente): trae Loyverse y descuenta lo pendiente de la tienda de esa lista.
  const refreshSales = async (sheet: ManagerCountSheet) => {
    if (busyId) return;
    setBusyId(sheet.id);
    setRefreshingId(sheet.id);
    setError(undefined);
    setNotice(undefined);
    setWarning(undefined);
    setBulkReport(null);
    try {
      const summary = salesRefreshText(await refreshCountSales(sheet.store.id));
      // Se recarga primero: la carga limpia los mensajes al empezar.
      await load();
      if (summary.problem) setWarning(summary.text);
      else setNotice(summary.text);
    } catch (refreshError) {
      reloadWithError(refreshSalesErrorMessage(refreshError));
    } finally {
      setBusyId(undefined);
      setRefreshingId(undefined);
    }
  };

  return (
    <section aria-label="Conteos" className="n3-screen waste-page">
      {/* P-075: el encabezado de la piel nueva (el `.page-header` de antes no tenía estilo). */}
      <PageHeader
        actions={manager
          ? <Button disabled={!stores.length} iconStart={<Icon name="plus" size={18} />} onClick={() => { setNotice(undefined); setCreateDraft({}); }} variant="primary">Nueva lista</Button>
          : undefined}
        description={manager
          ? 'Listas para contar a ciegas y las diferencias por aprobar. El inventario solo cambia al aprobar.'
          : 'Cuenta lo que te asignaron: escribe lo que ves en el estante. Tu gerente lo revisa.'}
        title="Conteos"
      />

      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {notice && <div className="alert alert--success" role="status">{notice}</div>}
      {warning && <div className="alert alert--warning" role="status">{warning}</div>}
      {bulkReport && (
        <div className={`alert ${bulkReport.left.length ? 'alert--warning' : 'alert--success'}`} role="status">
          <strong>{bulkReport.approved === 0 ? 'No se aprobó ningún conteo.' : bulkReport.approved === 1 ? 'Se aprobó 1 conteo.' : `Se aprobaron ${bulkReport.approved} conteos.`}</strong>
          {bulkReport.note && <> {bulkReport.note}</>}
          {bulkReport.left.length > 0 && <>
            {' '}Quedan esperando:
            <ul className="count-report-list">
              {bulkReport.left.map((item) => <li key={item.lineId}><strong>{item.ingredientName}:</strong> {item.message}</li>)}
            </ul>
          </>}
        </div>
      )}
      {loading && (list || mine ? <div className="inline-loading"><span className="spinner" />Actualizando…</div> : <LoadingPanel label="Cargando conteos…" />)}

      {openMine.map((sheet) => (
        <AssignedSheetCard
          busy={busyId === sheet.id}
          key={sheet.id}
          onCount={(line) => { setNotice(undefined); setCountTarget({ line, storeName: sheet.store.name }); }}
          onSubmit={() => void submit(sheet)}
          sheet={sheet}
          timeZone={timeZoneOf(sheet.store.id)}
        />
      ))}
      {!manager && mine && openMine.length === 0 && (
        <EmptyState compact icon="count" title="No tienes listas por contar" description="Cuando tu gerente te asigne una, aparecerá aquí y en tu Inicio." />
      )}
      {!manager && recentMine.map((sheet) => (
        <AssignedSheetCard busy={false} key={sheet.id} onCount={() => undefined} onSubmit={() => undefined} sheet={sheet} timeZone={timeZoneOf(sheet.store.id)} />
      ))}

      {/* P-075: las vistas del gerente con las pestañas de sección (antes, un `segmented`). La bandeja es UNA y
          se vuelve a pedir al cambiar de vista, como siempre. Las listas de arriba («Te toca contar») son las de
          quien entra, y van primero. Cada tarjeta es un renglón de la rejilla de la pantalla (separadas). */}
      {manager && <>
        <SectionTabs active={tab} label="Vistas de Conteos" onSelect={(id) => setTab(id as ManagerTab)} tabs={MANAGER_TABS.map(([id, label]) => ({ id, label }))} />
        {stores.length > 1 && (
          <div className="stock-filters">
            <label className="field field--inline"><span>Tienda</span>
              <select onChange={(event) => setStoreFilter(event.target.value)} value={storeFilter}>
                <option value="">Todas mis tiendas</option>
                {stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
              </select>
            </label>
          </div>
        )}
        {list && managerSheets.length === 0 && (tab === 'review'
          ? <EmptyState compact icon="count" title="No hay listas abiertas" description="Toca «Nueva lista» para pedir que alguien cuente." />
          : <EmptyState compact icon="count" title="No hay listas terminadas" description="Aquí aparecen las listas terminadas o canceladas de los últimos 30 días." />)}
        {managerSheets.map((sheet) => (
          <ManagerSheetCard
            busy={busyId === sheet.id}
            key={sheet.id}
            onApprove={(line) => { setNotice(undefined); setApproveTarget({ line, storeId: sheet.store.id }); }}
            onApproveAll={() => void approveAll(sheet)}
            onCancel={() => void cancel(sheet)}
            onRefreshSales={() => void refreshSales(sheet)}
            refreshing={refreshingId === sheet.id}
            salesNeed={salesRefreshNeed(sheet)}
            onRepeat={() => { setNotice(undefined); setCreateDraft(repeatDraft(sheet)); }}
            onReview={(line, mode) => { setNotice(undefined); setReviewTarget({ line, mode }); }}
            sheet={sheet}
            timeZone={timeZoneOf(sheet.store.id)}
          />
        ))}
        {/* El umbral se cambia en Inventario → Stock (ahí vive su diálogo); el dueño tiene el atajo. */}
        {list && <p className="wiz-hint">{countThresholdSummary(list.threshold)}{owner && <>{' '}<button className="link-button" onClick={() => { window.location.hash = 'inventory'; }} type="button">Cambiar en Inventario</button></>}</p>}
      </>}

      {createDraft && (
        <CountSheetCreateModal
          initial={createDraft}
          onClose={() => setCreateDraft(null)}
          onCreated={(sheet, replayed) => {
            setCreateDraft(null);
            setNotice(replayed ? 'Esa lista ya estaba creada; no se duplicó.' : `Lista asignada a ${sheet.assignedTo?.name ?? 'la persona'}: ${plural(sheet.lines.length, 'insumo', 'insumos')}. La verá en su Inicio.`);
            setTab('review');
            void load();
          }}
          stores={stores}
        />
      )}
      {countTarget && (
        <BlindCountModal
          line={countTarget.line}
          // Al cerrar se vuelve a leer: un intento que no se dejó contar cambia lo que ve el gerente.
          onClose={() => { setCountTarget(null); void load(); }}
          onSaved={(message) => { setCountTarget(null); setNotice(message); void load(); }}
          storeName={countTarget.storeName}
        />
      )}
      {approveTarget && (
        <CountLineApproveModal
          line={approveTarget.line}
          onApproved={(message) => { setApproveTarget(null); setNotice(message); void load(); }}
          onClose={() => setApproveTarget(null)}
          onSalesRefreshed={async () => {
            const listed = await load();
            return listed?.sheets.flatMap((sheet) => sheet.lines).find((item) => item.id === approveTarget.line.id) ?? null;
          }}
          onStale={(message) => { setApproveTarget(null); reloadWithError(message); }}
          onRequestRecount={() => { const { line } = approveTarget; setApproveTarget(null); setNotice(undefined); setReviewTarget({ line, mode: 'recount' }); }}
          storeId={approveTarget.storeId}
        />
      )}
      {reviewTarget && (
        <CountLineReviewModal
          line={reviewTarget.line}
          mode={reviewTarget.mode}
          onClose={() => setReviewTarget(null)}
          onDone={(message) => { setReviewTarget(null); setNotice(message); void load(); }}
          onStale={(message) => { setReviewTarget(null); reloadWithError(message); }}
        />
      )}
    </section>
  );
}

/** Una lista de quien cuenta: los insumos, su propio número y el botón de enviar. Nada del sistema. */
function AssignedSheetCard({ sheet, timeZone, busy, onCount, onSubmit }: {
  sheet: AssignedCountSheet;
  timeZone: string;
  busy: boolean;
  onCount: (line: AssignedCountLine) => void;
  onSubmit: () => void;
}) {
  const open = sheet.status === 'OPEN';
  const progress = assignedProgress(sheet);
  const submit = submitState(sheet);
  return (
    <section aria-label={`Lista de conteo de ${sheet.store.name}`} className="content-card">
      <div className="content-card__header">
        <div>
          <span className="eyebrow">{sheet.store.name}</span>
          <h2>{open ? 'Te toca contar' : 'Lista enviada'}</h2>
          <p className="wiz-hint">{sheet.createdBy ? `Te la asignó ${sheet.createdBy} · ` : ''}{formatWasteDateTime(sheet.createdAt, timeZone)}{sheet.note ? ` · ${sheet.note}` : ''}</p>
        </div>
        <StatusBadge variant={sheetBadgeVariant(sheet.status)}>{COUNT_SHEET_LABELS[sheet.status]}</StatusBadge>
      </div>
      <ul className="pending-rows">
        {sheet.lines.map((line) => (
          <li key={line.id}>
            <div className="pending-row pending-row--static">
              <span className={`pending-row__ic${line.blocked ? ' pending-row__ic--muted' : ''}`}><Icon name="count" size={18} /></span>
              <span className="pending-row__body">
                <strong>{line.ingredient.name}</strong>
                <span>{assignedLineText(line)}</span>
                {line.recount && <span>Tu gerente pidió recontar{line.recount.note ? `: ${line.recount.note}` : ''}</span>}
                {line.rejectedNote && <span>Rechazado: {line.rejectedNote}</span>}
              </span>
              {open && (line.status === 'PENDING' || line.status === 'COUNTED')
                ? <button className={`button ${line.status === 'COUNTED' ? 'button--ghost' : 'button--secondary'}`} disabled={busy} onClick={() => onCount(line)} type="button">{line.status === 'COUNTED' ? 'Cambiar' : line.blocked ? 'Intentar otra vez' : 'Contar'}</button>
                : <StatusBadge variant={lineBadgeVariant(line.status)}>{COUNT_LINE_LABELS[line.status]}</StatusBadge>}
            </div>
          </li>
        ))}
      </ul>
      {open && (
        <div className="count-sheet-actions">
          <p className="wiz-hint">{plural(progress.counted, 'contado', 'contados')} · {progress.toCount} por contar{progress.blocked ? ` · ${plural(progress.blocked, 'no se puede contar ahora', 'no se pueden contar ahora')}` : ''}</p>
          <button className="button button--primary" disabled={busy || !submit.canSubmit} onClick={onSubmit} type="button">{busy ? 'Enviando…' : 'Enviar a mi gerente'}</button>
          {submit.reason && <span className="wiz-hint">{submit.reason}</span>}
        </div>
      )}
    </section>
  );
}

function assignedLineText(line: AssignedCountLine): string {
  const own = line.countedQuantity !== null ? `Contaste ${formatWasteQuantity(line.countedQuantity, line.unit.symbol)}` : null;
  if (line.status === 'PENDING') return line.blocked ? 'No se puede contar ahora. Sigue con los demás; tu gerente ya lo puede ver.' : `Por contar (en ${line.unit.name})`;
  if (line.status === 'COUNTED' || line.status === 'SUBMITTED' || line.status === 'APPROVED') return own ?? COUNT_LINE_LABELS[line.status];
  return COUNT_LINE_LABELS[line.status];
}

/** Una lista en la bandeja del gerente: cada insumo con lo contado, lo que decía el sistema y qué hacer. */
function ManagerSheetCard({ sheet, timeZone, busy, refreshing, salesNeed, onApprove, onReview, onApproveAll, onCancel, onRepeat, onRefreshSales }: {
  sheet: ManagerCountSheet;
  timeZone: string;
  busy: boolean;
  refreshing: boolean;
  /** Por qué conviene «Actualizar ventas» en esta lista; null = no hace falta y el botón no sale. */
  salesNeed: string | null;
  onApprove: (line: ManagerCountLine) => void;
  onReview: (line: ManagerCountLine, mode: 'reject' | 'recount') => void;
  onApproveAll: () => void;
  onCancel: () => void;
  onRepeat: () => void;
  onRefreshSales: () => void;
}) {
  // «Aprobar todo» solo aprueba lo que no tiene diferencia grande: el número dice cuántos de verdad.
  const bulk = approvableInBulk(sheet);
  return (
    <section aria-label={`Lista de ${sheet.assignedTo?.name ?? 'conteo'} en ${sheet.store.name}`} className="content-card">
      <div className="content-card__header">
        <div>
          <span className="eyebrow">{sheet.store.name}{sheet.store.isActive ? '' : ' (desactivada)'}</span>
          <h2>{sheet.assignedTo?.name ?? 'Sin asignar'}</h2>
          <p className="wiz-hint">
            {sheetProgressText(sheet.counts)} · creada {formatWasteDateTime(sheet.createdAt, timeZone)}{sheet.createdBy ? ` por ${sheet.createdBy.name}` : ''}
            {sheet.note ? ` · ${sheet.note}` : ''}
            {sheet.cancelledAt ? ` · cancelada${sheet.cancelledBy ? ` por ${sheet.cancelledBy.name}` : ''}` : ''}
          </p>
        </div>
        <StatusBadge variant={sheetBadgeVariant(sheet.status)}>{COUNT_SHEET_LABELS[sheet.status]}</StatusBadge>
      </div>
      <ul className="pending-rows">
        {sheet.lines.map((line) => <ManagerLineRow busy={busy} key={line.id} line={line} onApprove={onApprove} onReview={onReview} timeZone={timeZone} />)}
      </ul>
      {salesNeed && (
        <div className="count-refresh">
          <button className="button button--secondary" disabled={busy} onClick={onRefreshSales} type="button">{refreshing ? 'Actualizando ventas…' : 'Actualizar ventas'}</button>
          <span className="wiz-hint">{salesNeed}</span>
        </div>
      )}
      {/* P-078 (ActionBar v2 · H): las mismas tres, por intención: «Aprobar todo» como primaria, Repetir como secundaria y
          «Cancelar lista» aparte. */}
      <ActionBar aria-label={`Acciones de la lista de ${sheet.store.name}`} className="count-sheet-actions" danger={sheet.status === 'OPEN' ? <button className="button button--ghost" disabled={busy} onClick={onCancel} type="button">Cancelar lista</button> : undefined} primary={bulk > 0 ? <button className="button button--primary" disabled={busy} onClick={onApproveAll} type="button">{busy && !refreshing ? 'Aprobando…' : `Aprobar todo (${bulk})`}</button> : undefined}>
        <button className="button button--ghost" disabled={busy} onClick={onRepeat} type="button">Repetir</button>
      </ActionBar>
    </section>
  );
}

function ManagerLineRow({ line, timeZone, busy, onApprove, onReview }: {
  line: ManagerCountLine;
  timeZone: string;
  busy: boolean;
  onApprove: (line: ManagerCountLine) => void;
  onReview: (line: ManagerCountLine, mode: 'reject' | 'recount') => void;
}) {
  const details: string[] = [];
  const counted = countedText(line);
  if (line.status === 'PENDING') details.push(line.blocked?.message ?? 'Por contar');
  if (line.status === 'COUNTED') details.push('Contado; falta que envíe la lista');
  if (counted && line.status !== 'COUNTED' && line.status !== 'PENDING') {
    details.push(`${counted}${line.countedAt ? ` · ${formatWasteDateTime(line.countedAt, timeZone)}` : ''}`);
  }
  if (line.status === 'SUBMITTED') {
    const late = atApprovalText(line);
    if (late) details.push(late);
    if (line.reportedWasteSameDay && line.reportedWasteSameDay.count > 0) details.push(`Mermas registradas ese día: ${line.reportedWasteSameDay.count} (${formatWasteQuantity(line.reportedWasteSameDay.quantity, line.unit.symbol)})`);
  }
  if (line.status === 'APPROVED') {
    const applied = appliedText(line);
    if (applied) details.push(`${applied}${line.review?.by ? ` Aprobó ${line.review.by.name}.` : ''}`);
    if (line.review?.note) details.push(`Motivo: ${line.review.note}`);
  }
  if ((line.status === 'REJECTED' || line.status === 'RECOUNT') && line.review?.note) details.push(`${line.status === 'REJECTED' ? 'Motivo' : 'Nota'}: ${line.review.note}`);
  const large = line.status === 'SUBMITTED' && Boolean(approvalComparison(line)?.large);
  return (
    <li>
      <div className="pending-row pending-row--static waste-row">
        <span className={`pending-row__ic${large ? ' pending-row__ic--danger' : ''}`}><Icon name={large ? 'warning' : 'count'} size={18} /></span>
        <span className="pending-row__body">
          <strong>{line.ingredient.name}{line.recountOfLineId ? ' · recuento' : ''}</strong>
          {details.map((detail) => <span key={detail}>{detail}</span>)}
          {large && <span className="waste-row__flags"><StatusBadge variant="danger">Diferencia grande</StatusBadge></span>}
          {line.status === 'SUBMITTED' && (
            <span className="count-line-actions">
              <button className="link-button" disabled={busy} onClick={() => onApprove(line)} type="button">Aprobar</button>
              <button className="link-button" disabled={busy} onClick={() => onReview(line, 'recount')} type="button">Pedir recontar</button>
              {/* P-078 (H): rechazar se distingue de aprobar (texto rojo), sin moverse de la fila. */}
              <button className="link-button text-button--danger" disabled={busy} onClick={() => onReview(line, 'reject')} type="button">Rechazar</button>
            </span>
          )}
        </span>
        <StatusBadge variant={lineBadgeVariant(line.status)}>{COUNT_LINE_LABELS[line.status]}</StatusBadge>
      </div>
    </li>
  );
}
